import React from 'react';
import { Box } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import type { ScorecardThresholdFieldProps } from './ScorecardThresholdField.interface';
import { ScorecardThresholdSelector } from './ScorecardThresholdSelector';
import { selectUsers } from '@/store/users/selectors';

const ROLE_GROUP_IDS = [
  'job_coordinator',
  'job_recruiter',
  'job_hiring_manager',
];

/**
 * Drop-in FormBuilder custom Field for the scorecard completion threshold.
 * Handles the role-group detection logic in one place so every form
 * (assessment, review, coding-assessment) behaves consistently.
 *
 * Gate inclusion with `feScorecardThreshold` at each FormBuilder config.
 *
 * Usage in a FormBuilder config:
 *   { name: 'scorecard_completion_threshold', type: 'custom', Field: ScorecardThresholdField }
 *
 * By default it reads `formik.values.reviewer_ids`.
 * Pass reviewerIdsKey="participant_ids" for review forms that use that field name.
 */
export const ScorecardThresholdField: React.FC<
  ScorecardThresholdFieldProps
> = ({
  formik,
  reviewerIdsKey = 'reviewer_ids',
  useCriteriaCorpSpacing = false,
  containerStyles = {},
}) => {
  const activeUsers = useSelector(selectUsers) ?? [];
  const activeUserIds = new Set(activeUsers.map((u) => String(u?.id)));

  const reviewerIds: (string | number)[] = formik.values[reviewerIdsKey] ?? [];
  const hasGroupReviewers = reviewerIds.some((id) =>
    ROLE_GROUP_IDS.includes(String(id))
  );

  // Only count active individual reviewers; deleted users remain in the list
  // on the backend but should not be counted toward the threshold display.
  const activeReviewerCount = reviewerIds.filter((id) => {
    const idStr = String(id);
    return ROLE_GROUP_IDS.includes(idStr) ? false : activeUserIds.has(idStr);
  }).length;

  if (activeReviewerCount <= 1 && !hasGroupReviewers) return null;

  return (
    <Box
      width="100%"
      {...(useCriteriaCorpSpacing ? { px: '6', sx: { margin: '1em 0' } } : {})}
      {...containerStyles}
    >
      <ScorecardThresholdSelector
        value={formik.values.scorecard_completion_threshold}
        onChange={(val) =>
          formik.setFieldValue('scorecard_completion_threshold', val)
        }
        reviewerCount={activeReviewerCount}
        hasGroupReviewers={hasGroupReviewers}
      />
    </Box>
  );
};
